import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import APIService from '../APIService';
import { message } from 'antd';
import type { StoryPost, StoryOptions } from '../../models/Story';
import { EntityTypes, PINNED_MESSAGE, UNPINNED_MESSAGE } from '../../constants';
import type Story from '../../models/Story';
import { updateQueryDataStoryItem } from '../../utils/Utils';

const DEFAULT_SIZE = 30;

interface UseEntityParams {
  entityId?: string;
  entityType?: string;
  size?: number;
  from?: number;
  query?: string;
  filters?: any;
}

const usePosts = (
  queryKey,
  { entityId, entityType, size = DEFAULT_SIZE, from = 0, query = '', filters = {} }: UseEntityParams,
) => {
  return useInfiniteQuery({
    queryKey: [...queryKey, JSON.stringify(filters)],
    queryFn: async ({ pageParam }) => {
      const entity = await APIService.POST(
        `/${entityType}/${entityId}/searchposts?size=${size}&from=${pageParam * size}`,
        undefined,
        {
          filters,
          query,
          searchAll: true,
        },
      );
      return entity.data;
    },
    initialPageParam: from,
    getNextPageParam: (lastPage, allPages) => {
      const fetchedCount = allPages.length * size;
      const nextPage = (allPages.length * size) / size;
      return fetchedCount < lastPage.total ? nextPage : undefined;
    },
    enabled: !!entityId && !!entityType,
  });
};

const useRecommendedPosts = ({ entityId, dismissed, currentRecommended }) => {
  return useQuery<Story[]>({
    queryKey: [EntityTypes.BOARD, entityId, 'recommended', dismissed],
    queryFn: async () => {
      const options = { currentRecommended };
      if (dismissed) {
        options['dismissed'] = dismissed;
      }
      const entity = await APIService.POST(`/${EntityTypes.BOARD}/${entityId}/searchrecommended`, undefined, options);
      return entity.data?.hits;
    },
    enabled: !!entityId,
  });
};

type Page = { hits: Story[]; total: number };
type Pages = { pages: Page[] };

const usePostsDelete = ({ entityId, entityType, folderId = '', queryKey }) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (posts: string[]) => {
      const res = await APIService.POST(`/${entityType}/${entityId}/posts/delete`, undefined, { posts, folderId });
      return res.data;
    },
    onMutate: async (posts: string[]) => {
      await queryClient.cancelQueries(queryKey);
      const previousPosts = queryClient.getQueryData(queryKey);
      queryClient.setQueryData<Pages>(queryKey, (old) => {
        if (!old) return;
        return {
          ...old,
          pages: old.pages.map((page) => {
            return {
              ...page,
              hits: page.hits.filter((post) => !posts.includes(post.id)),
            };
          }),
        };
      });
      return { previousPosts };
    },
    onSuccess: () => {
      message.success('Posts successfully deleted.');
    },
    onError: (err, posts, context) => {
      message.error('Posts could not be deleted. Please try again.');
      queryClient.setQueryData(queryKey, context?.previousPosts);
    },
    onSettled: async () => {
      return await queryClient.invalidateQueries({ queryKey });
    },
  });
};

const usePostUpdateMain = ({ entityId, boardId, storyId }) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (storyPost: StoryPost) => {
      const data = { data: { ...storyPost } };
      const res = await APIService.PUT(`/board/${entityId}/post/${storyId}`, undefined, data);
      return res.data;
    },
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: [
          entityId,
          EntityTypes.BOARD,
          boardId,
          30,
          0,
          { folder: [boardId], order: 'desc', postsShown: 'pinned' },
          '',
        ],
      });
    },
  });
};

const usePostUpdate = ({ entityId, boardId, storyId }) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (storyOptions: StoryOptions) => {
      const res = await APIService.PUT(`/board/${entityId}/post/${storyId}`, undefined, storyOptions);
      return res.data;
    },
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: [
          entityId,
          EntityTypes.BOARD,
          boardId,
          30,
          0,
          { folder: [boardId], order: 'desc', postsShown: 'pinned' },
          '',
        ],
      });
    },
  });
};

const useAddFoldersToStories = ({
  entityId,
  entityType,
  queryKey,
}: {
  entityId: string;
  entityType: string;
  queryKey?: any[];
}) => {
  const { mutate } = useAddFoldersToStory({ entityId, entityType, queryKey });
  return async ({ storyIds, folderIds }) => {
    const results = storyIds.map((storyId) => {
      return mutate(
        { storyId, folderIds },
        {
          onSuccess: () => {
            message.success(PINNED_MESSAGE);
          },
        },
      );
    });

    return Promise.all(results);
  };
};

const useAddFoldersToStory = ({ entityId, entityType, queryKey }) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ storyId, folderIds }: { storyId: string; folderIds: string[] }) => {
      const requests = folderIds.map((folderId) =>
        APIService.PUT(
          `/${entityType}/${entityId}/folder/${folderId}/posts?newPosition=0&currentPosition=undefined`,
          undefined,
          { storyIds: [storyId] },
        ),
      );
      return Promise.all(requests);
    },
    onSettled: async () => {
      return await queryClient.invalidateQueries({ queryKey: queryKey || [entityId, entityType] });
    },
  });
};

const useRemoveFoldersFromStories = ({
  entityId,
  entityType,
  queryKey,
}: {
  entityId: string;
  entityType: string;
  queryKey?: any[];
}) => {
  const { mutate } = useRemoveFoldersFromStory({ entityId, entityType });
  const queryClient = useQueryClient();
  return async ({ storyIds, folderIds }) => {
    const results = storyIds.map((storyId) => {
      return mutate(
        { storyId, folderIds },
        {
          onSuccess: () => {
            message.success(UNPINNED_MESSAGE);
            queryClient.invalidateQueries({ queryKey: queryKey || [entityId, entityType] });
          },
        },
      );
    });

    return Promise.all(results);
  };
};

const useRemoveFoldersFromStory = ({ entityId, entityType }) => {
  return useMutation({
    mutationFn: async ({ storyId, folderIds }: { storyId: string; folderIds: string[] }) => {
      const requests = folderIds.map((folderId) =>
        APIService.DELETE(`/${entityType}/${entityId}/folder/${folderId}/${storyId}`),
      );
      return Promise.all(requests);
    },
  });
};

const useEditPost = ({ entityId, entityType }) => {
  return useMutation({
    mutationFn: async ({ postId, payload }: { postId: string; payload: { [key: string]: unknown } }) => {
      const res = await APIService.PUT(`/${entityType}/${entityId}/posts/${postId}`, undefined, { data: payload });
      return res.data;
    },
  });
};

const useMoveStoryPosition = ({ entityId, entityType, folderId, queryKey }) => {
  const queryClient = useQueryClient();
  const size = queryKey[3];
  return useMutation({
    mutationFn: async ({
      storyId,
      position,
      currentPosition,
    }: {
      storyId: string;
      position: number;
      currentPosition: number;
    }) => {
      const res = await APIService.PUT(
        `/${entityType}/${entityId}/folder/${folderId}/posts?newPosition=${position}&currentPosition=${currentPosition}`,
        undefined,
        { storyIds: [storyId] },
      );
      return res.data;
    },
    onMutate: async ({ storyId, position, currentPosition }) => {
      await queryClient.cancelQueries(queryKey);
      const previousPosts = queryClient.getQueryData(queryKey);
      queryClient.setQueryData<Pages>(queryKey, (old) => {
        if (!old) return;
        // create a single array of hits
        const hits = old.pages.map((page) => page.hits).flat();
        let newHits = [...hits];
        // find the index of the story to move
        const storyIndex = hits.findIndex((post) => post.id === storyId);
        if (storyIndex === -1) return;

        if (storyIndex < position) {
          newHits = [
            ...hits.slice(0, storyIndex),
            ...hits.slice(storyIndex + 1, position + 1),
            hits[storyIndex],
            ...hits.slice(position + 1),
          ];
        } else {
          newHits = [
            ...hits.slice(0, position),
            hits[storyIndex],
            ...hits.slice(position, storyIndex),
            ...hits.slice(storyIndex + 1),
          ];
        }
        // create new pages
        const newPages: Page[] = [];
        let i = 0;
        while (i < hits.length) {
          const newPage = { hits: newHits.slice(i, i + size || DEFAULT_SIZE), total: newHits.length };
          newPages.push(newPage);
          i += size || DEFAULT_SIZE;
        }
        return { ...old, pages: newPages };
      });
      return { previousPosts };
    },
    onError: (err, posts, context) => {
      message.error('Post could not be moved. Please try again.');
      queryClient.setQueryData(queryKey, context?.previousPosts);
    },
    onSettled: async () => {
      return await queryClient.invalidateQueries({ queryKey });
    },
  });
};

const useMarkPostsRead = ({ entityId, entityType, queryKey }) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (storyIds: string[]) => {
      const res = await APIService.PUT(`/${entityType}/${entityId}/posts/update`, undefined, {
        posts: storyIds,
        read: true,
      });
      return res.data;
    },
    onMutate: async (storyIds: string[]) => {
      return await updateQueryDataStoryItem(queryClient, queryKey, storyIds[0], (prev) => ({
        ...prev,
        read: true,
      }));
    },
  });
};

export {
  usePostsDelete,
  usePostUpdateMain,
  usePostUpdate,
  useAddFoldersToStory,
  useAddFoldersToStories,
  useRemoveFoldersFromStories,
  useEditPost,
  useMoveStoryPosition,
  useMarkPostsRead,
  useRecommendedPosts,
};

export default usePosts;
